/**
 * 接口类型契约 — 手写自 docs/api/openapi.yaml（OpenAPI 3.1.0，53 路径 / 74 操作 / 103 schema）。
 *
 * 规则：
 * - 字段名、可空性、枚举值必须与 openapi.yaml 逐字一致；不一致即为契约漂移，须先改 openapi 再改这里。
 * - 所有时间戳为 **epoch 秒（int64，UTC）**，不是毫秒。前端展示前统一走 lib/format.ts 转换。
 * - 统一响应体 `{ code, data, message }`，`code === 0` 为成功（openapi.yaml info §1）。
 */

/* ── 通用外壳 ── */

export interface ApiEnvelope<T> {
  code: number;
  data: T;
  message: string;
}

export interface PageMeta {
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export type Paged<T> = PageMeta & { items: T[] };

export interface ValidationIssue {
  path: string;
  message: string;
}

/* ── 枚举（openapi #/components/schemas 同名节点） ── */

export const LICENSE_CODE_STATUSES = ['available', 'exported', 'redeemed', 'void'] as const;
export type LicenseCodeStatus = (typeof LICENSE_CODE_STATUSES)[number];

export const LICENSE_STATUSES = ['active', 'suspended', 'revoked', 'expired'] as const;
export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

export const ACTIVATION_STATUSES = ['active', 'deactivated'] as const;
export type ActivationStatus = (typeof ACTIVATION_STATUSES)[number];

export const ADMIN_ROLES = ['owner', 'operator', 'viewer'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ANOMALY_KINDS = [
  'many_devices_one_license',
  'fingerprint_reused_across_licenses',
  'frequency_spike',
] as const;
export type AnomalyKind = (typeof ANOMALY_KINDS)[number];

/* ── 健康探针 ── */

export interface HealthData {
  status: 'ok';
  version: string;
}

export interface ReadinessData {
  api: 'up' | 'down';
  database: 'up' | 'down';
  signer: 'up' | 'down';
  /** 当前架构未部署任务队列，如实上报 not_configured */
  queue: 'up' | 'down' | 'not_configured';
  /** 当前未部署备份流水线，如实上报 not_configured */
  backup: 'up' | 'down' | 'not_configured';
}

/* ── 目录域 ── */

export interface ProductIntegration {
  /** 产品 UUID，对应下游 LH_PRODUCT_ID */
  product_id: string;
  /** 根公钥（32 字节 raw base64url，JWK `x`），对应下游 LH_ROOT_PUBLIC_KEY；根未配置时为 null */
  root_public_key: string | null;
  /** 对外可达 API 基址（含 /api/v1），对应下游 LH_BASE */
  base_url: string;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  /** 任何撤销/暂停操作递增，供客户端廉价探测变更（架构 §4.2） */
  revocation_list_version: number;
  created_at: number;
  updated_at?: number;
}

/** 产品版本（openapi ProductVersion）：纯元数据，不接入授权判定路径 */
export interface ProductVersion {
  id: string;
  product_id: string;
  version: string;
  channel: 'stable' | 'beta';
  release_notes: string | null;
  download_url: string | null;
  released_at: number | null;
  created_at: number;
}

export type EntitlementValue = boolean | number | string;

export interface Plan {
  id: string;
  product_id: string;
  code: string;
  name: string;
  /** null = 永久授权；undefined = API 未返回该字段（防御渲染见各页面） */
  duration_days: number | null | undefined;
  max_devices: number | undefined;
  /** 秒，下限 21600（R-21 在 DB 层强制） */
  offline_grace_seconds: number | undefined;
  version_constraint: string | null;
  entitlements: Record<string, EntitlementValue>;
  is_active: boolean;
  created_at: number;
}

/* ── 库存域 ── */

export interface CodeBatchStats {
  available: number;
  exported: number;
  redeemed: number;
  void: number;
}

export interface CodeBatch {
  id: string;
  product_id: string;
  plan_id: string;
  batch_no: string;
  quantity: number;
  note: string | null;
  /** > 0 即不可再导（PRD §10.2「CSV 只能导出一次」） */
  export_count: number;
  first_exported_at: number | null;
  exported_by: string | null;
  stats?: CodeBatchStats;
  created_at: number;
}

export interface LicenseCode {
  id: string;
  product_id: string;
  plan_id: string;
  batch_id: string;
  /** 掩码展示，明文不可查 */
  code_mask: string;
  status: LicenseCodeStatus;
  redeemed_at: number | null;
  void_reason: string | null;
  created_at: number;
}

/* ── 授权域 ── */

export interface PlanSnapshot {
  plan_id: string;
  plan_code: string;
  plan_name: string;
  duration_days: number | null;
  max_devices: number;
  offline_grace_seconds: number;
  version_constraint: string | null;
  entitlements: Record<string, EntitlementValue>;
  snapshot_at: number;
}

export interface License {
  id: string;
  product_id: string;
  plan_id: string;
  license_code_id: string | null;
  customer_id: string | null;
  order_id: string | null;
  status: LicenseStatus;
  /** 兑换时刻的套餐快照，权益判定以此为准，不实时 JOIN plans（架构 §4.5） */
  plan_snapshot: PlanSnapshot;
  issued_at: number;
  /** null = 永久 */
  expires_at: number | null;
  revoked_at: number | null;
  suspended_at: number | null;
  status_reason: string | null;
  activated_device_count: number;
  note: string | null;
  created_at: number;
  updated_at: number;
}

/**
 * 管理员手动创建授权（P-12，不消耗库存码）。对应 `POST /admin/licenses` 的请求体。
 * `product_id` 与 `plan_id` 必填；其余可选。`expires_at` 为 null 表示永久授权。
 */
export interface CreateLicenseRequest {
  product_id: string;
  plan_id: string;
  customer_id?: string | null;
  /** 来源订单号（openapi createLicenseManually.order_id，关联 orders 表） */
  order_id?: string | null;
  /** epoch 秒（UTC）；null = 永久 */
  expires_at?: number | null;
  note?: string | null;
}

export interface Activation {
  id: string;
  license_id: string;
  product_id: string;
  device_hash: string;
  device_name: string | null;
  status: ActivationStatus;
  activated_at: number;
  last_seen_at: number;
  deactivated_at: number | null;
  deactivated_by: 'user' | 'admin' | 'system' | null;
}

/* ── 授权详情（GET /admin/licenses/{id}） ── */

/** 详情 = 授权本体 + 其激活设备 + 关联客户（openapi LicenseDetailResponse） */
export interface LicenseDetail extends License {
  activations: Activation[];
  customer: Customer | null;
}

/**
 * 撤销/暂停的返回体（RevokeResultResponse）。
 * `expected_offline_expiry_at` 是这两个端点存在的**关键理由**：撤销不是立即全网生效，
 * 离线客户端最迟在 `now + offline_grace_seconds` 才失效，UI 必须把这个时刻显示给运营。
 */
export interface RevokeResult {
  license: License;
  expected_offline_expiry_at: number;
  offline_grace_seconds: number;
  online_effect: 'immediate_on_next_validate';
}

/**
 * 兑换尝试的只读记录，对应 `redemption_requests` 表（docs/02-ARCHITECTURE.md §4.8.6）。
 *
 * 服务端**不存授权码明文**（设计约束），故「授权码」列只能用 `request_digest`
 * （请求体 SHA-256，64 hex）作为指纹展示，可唯一标识「同码 + 同设备 + 同产品」的尝试。
 * 排查「同一码被尝试多次」即按 `request_digest` 分组。
 */
export interface RedemptionRequest {
  id: string;
  product_id: string;
  /** 客户端指纹：客户端生成的幂等键（openapi §Idempotency-Key），排查重试来源 */
  idempotency_key: string;
  /** 请求体 SHA-256（64 hex）。授权码指纹，不存明文时的唯一可分组依据 */
  request_digest: string;
  /** 关联授权；code_not_found 等失败尝试为 null */
  license_id: string | null;
  /** HTTP 状态码：200 兑换成功 / 409 已兑换·幂等回放 / 4xx·5xx 失败 */
  status_code: number;
  /** 落库的响应体（成功信封或签名否定裁决的 JSON 文本） */
  response_body: string;
  created_at: number;
  expires_at: number | null;
}

/**
 * 功能开关键类型（openapi EntitlementKey.value_type enum）。
 * 决定注册表里「默认值」输入控件，以及套餐里「权益值」的解析口径。
 */
export type EntitlementValueType = 'boolean' | 'integer' | 'string';

/**
 * 功能开关键定义（openapi EntitlementKey，对应 `entitlement_keys` 表）。
 *
 * 这是「能授予什么能力」的**全局边界**——`plans.entitlements` JSONB 只存值，
 * 这里存键的元信息（类型 / 展示名 / 默认值）。没有这张表，后台渲染不出权益表单，
 * 也回答不了「哪些套餐开了功能 X」。注意 `key` 一旦创建**不可改名**（openapi 注明），
 * 要改请新建 + 迁移 + 删除旧键。
 */
export interface EntitlementKey {
  id: string;
  product_id: string;
  /** 资源标识，pattern `^[a-z0-9][a-z0-9_]{1,62}$`，大小写敏感 */
  key: string;
  value_type: EntitlementValueType;
  display_name: string;
  default_value: EntitlementValue | null;
  created_at: number;
}

/** 注册功能开关键（openapi CreateEntitlementKeyRequest，additionalProperties:false） */
export interface CreateEntitlementKeyRequest {
  key: string;
  value_type: EntitlementValueType;
  display_name: string;
  default_value?: EntitlementValue | null;
}

/** 修改功能开关键（openapi UpdateEntitlementKeyRequest，additionalProperties:false）。`key` 不可改 */
export interface UpdateEntitlementKeyRequest {
  display_name?: string;
  default_value?: EntitlementValue | null;
}

export interface Customer {
  id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  note: string | null;
  created_at: number;
}

/** 登记/编辑客户（admin-crm，openapi customerSchema）。字段全部可空，但至少应提供 name 或 email。 */
export interface CustomerInput {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  note?: string | null;
}

/** 登记订单（admin-crm，openapi createOrderSchema）。source 固定 manual（后台手动登记）。 */
export interface CreateOrderRequest {
  product_id: string;
  customer_id?: string | null;
  source: 'manual';
  external_order_no?: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  note?: string | null;
}

export interface Order {
  id: string;
  product_id: string;
  customer_id: string | null;
  source: string | null;
  external_order_no: string | null;
  amount_cents: number | null;
  currency: string | null;
  note: string | null;
  created_at: number;
}

export interface ApiKey {
  id: string;
  /** 契约字段（openapi ApiKey.name） */
  name: string;
  /** 契约字段（openapi ApiKey.key_prefix），如 lhk_A7K3M */
  key_prefix: string;
  role: AdminRole;
  /** 作用域元数据（openapi AdminScope；MVP 阶段不参与鉴权，鉴权按 role） */
  scopes: string[];
  last_used_at: number | null;
  /** 过期时间（openapi ApiKey.expires_at）；null = 永不过期 */
  expires_at: number | null;
  created_at: number;
}

/**
 * 作用域矩阵（资源 × 读写，openapi AdminScope 的 UI 形态）。
 * 三端同步：openapi AdminScope 枚举 ↔ 后端 api-key-scopes.ts ↔ 本矩阵。
 */
export const SCOPE_MATRIX: ReadonlyArray<{ resource: string; label: string; ops: readonly ('read' | 'write')[] }> = [
  { resource: 'products', label: '产品', ops: ['read', 'write'] },
  { resource: 'plans', label: '套餐', ops: ['read', 'write'] },
  { resource: 'licenses', label: '授权', ops: ['read', 'write'] },
  { resource: 'codes', label: '授权码', ops: ['read', 'write'] },
  { resource: 'customers', label: '客户与订单', ops: ['read', 'write'] },
  { resource: 'audit', label: '审计', ops: ['read'] },
  { resource: 'stats', label: '统计', ops: ['read'] },
  { resource: 'keys', label: 'API Key', ops: ['write'] },
  { resource: 'sessions', label: '会话', ops: ['write'] },
  { resource: 'signing_keys', label: '签名密钥', ops: ['write'] },
];

/** 创建 API Key 的响应（openapi CreateApiKeyResultResponse.data）——明文只出现一次 */
export interface CreateApiKeyResult {
  api_key: ApiKey;
  plaintext: string;
}

export interface Session {
  id: string;
  created_at: number;
  expires_at: number;
  last_seen_at: number;
  ip: string;
  user_agent: string;
  is_current: boolean;
}

/* ── 管理端认证（openapi paths /admin/auth/*） ── */

/** GET /admin/auth/csrf → CsrfResponse.data */
export interface CsrfData {
  csrf_token: string;
}

/** POST /admin/auth/login 请求体（openapi LoginRequest，additionalProperties: false） */
export interface LoginRequest {
  email: string;
  /** minLength 12 —— 与服务端 zod 一致，前端先挡一层，省一次 400 往返 */
  password: string;
  /** 6 位数字。当前后端未启用 TOTP，字段保留以对齐契约 */
  totp_code?: string | null;
}

/** openapi LoginRequest.password 的 minLength，前端校验与服务端保持同一个数 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
export const EMAIL_MAX_LENGTH = 254;

/* ── IAM ── */

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  is_active: boolean;
  last_login_at: number | null;
  created_at: number;
}

/* ── 统计（GET /admin/stats/overview） ── */

export interface StatsOverview {
  licenses: {
    active: number;
    suspended: number;
    revoked: number;
    expired: number;
    expiring_in_7d: number;
  };
  inventory: {
    available: number;
    exported: number;
    redeemed: number;
    void: number;
  };
  activations: {
    active_devices: number;
    seen_24h: number;
  };
  validations_24h: {
    total: number;
    allow: number;
    deny: number;
    error: number;
    p95_latency_ms: number;
  };
  open_anomalies?: number;
}

/* ── 库存水位（GET /admin/products/{product_id}/inventory，OD-017） ──
   注意：该路径**尚未写入 docs/api/openapi.yaml**（后端 Sprint-2 新增）。
   下面的形状按 `CodeBatchStats` 四态 + 产品标识推定，并在 endpoints.ts 里做**运行时校验**：
   形状不符一律降级为「端点未就绪」空态，绝不把猜测当成事实渲染（失效模式 #3 幻觉接口）。
   契约补齐后请把本段替换为从 openapi 逐字抄写的定义。 */

export interface ProductInventory {
  product_id: string;
  /** 后端可能只回 id；缺失时由前端用产品列表补名字 */
  product_name?: string;
  available: number;
  exported: number;
  redeemed: number;
  void: number;
  /** 可选：服务端下发的低水位阈值。缺失时前端用本地默认值 */
  low_watermark?: number;
}

/** P-02 库存水位区块的行视图（由 ProductInventory + 产品名聚合而成） */
export interface InventoryRow {
  readonly product_id: string;
  readonly product_name: string;
  /** 未导出、仍留存在系统内的可兑换库存 */
  readonly available: number;
  /** 已导出到发卡站但尚未被兑换的库存，仍计入可售 */
  readonly exported: number;
  /** 已被客户兑换为 License 的库存 */
  readonly redeemed: number;
  /** 已作废的库存 */
  readonly void: number;
  /** 生效的低水位阈值：服务端下发优先，否则用 `DEFAULT_INVENTORY_LOW_WATERMARK` */
  readonly low_watermark: number;
}

/**
 * 低水位默认阈值。服务端在 `ProductInventory.low_watermark` 下发时以服务端为准 ——
 * 阈值属于运营策略，硬编码在前端意味着改一次要发一次版。
 */
export const DEFAULT_INVENTORY_LOW_WATERMARK = 200;

/* ── 列表查询参数 ── */

export interface ListLicensesQuery {
  product_id?: string;
  status?: LicenseStatus;
  plan_id?: string;
  customer_id?: string;
  /** epoch 秒 */
  expires_before?: number;
  q?: string;
  sort?: 'created_at' | 'updated_at' | 'expires_at';
  order?: 'asc' | 'desc';
  page?: number;
  /** 1..200，默认 20 */
  limit?: number;
}

export interface ListProductsQuery {
  q?: string;
  page?: number;
  limit?: number;
}

/* ── 安全：IP 黑名单（P-23 衍生） ── */

/**
 * 单条 IP 封禁记录。与后端 `GET /security/ip-blacklist` 的逐元素字段对齐。
 * 时间戳均为 **epoch 秒（int64，UTC）**，展示前走 lib/format.ts。
 * `expired` 由后端实时计算（过期条目仍可能在内存/表中残留，直到清理定时器扫掉）。
 */
export interface IpBlacklistEntry {
  /** 被封禁的 IP（精确单 IP，非脱敏网段） */
  ip: string;
  /** 封禁触发原因（见下方 REASON_LABEL 映射） */
  reason: string;
  /** 封禁创建时间（epoch 秒） */
  created_at: number;
  /** 过期时间（epoch 秒）；null = 永久封禁 */
  expires_at: number | null;
  /** 是否已过期（后端实时判定） */
  expired: boolean;
}

/** 封禁原因 → 中文标签（与后端 ip-blacklist.service 的 add() 文案一致） */
export const IP_BAN_REASON_LABEL: Record<string, string> = {
  global_rate_limit: '全局限流超额',
  redeem_ip_flood: '兑换接口刷屏',
  validate_ip_flood: '验证接口刷屏',
  heartbeat_ip_flood: '心跳接口刷屏',
  login_bruteforce: '登录爆破',
};

export function ipBanReasonLabel(reason: string): string {
  return IP_BAN_REASON_LABEL[reason] ?? reason;
}
